/*
 * Project: Goalscape
 * File:    CardModal.js
 * Path:    src/components/CardModal.js
 * Description: Modal component for displaying and editing card details.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement optimistic updates for card changes to improve UI responsiveness.
 */
import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { DragDropContext } from '@hello-pangea/dnd';
import { db, getCoverImage, addCoverImage, deleteCoverImage } from '../firebase'; // Import Firebase cover image functions
import { useAuth } from '../contexts/AuthContext';
import { PRIORITY_LEVELS } from '../constants/cardConstants';
import { addActivity, duplicateCard } from '../utils/cardUtils';

import CardHeader from './card/CardHeader';
import CardDescription from './card/CardDescription';
import CardChecklists from './card/CardChecklists';
import CardActivity from './card/CardActivity';
import CardSidebar from './card/CardSidebar';
import LabelPicker from './LabelPicker';
import AssigneePicker from './AssigneePicker';
import ConfirmationModal from './ConfirmationModal';
import AttachmentSection from './AttachmentSection';
import CardDueDate from './card/CardDueDate'; // Import the new component
import CardStartDate from './card/CardStartDate';
import MoveCardModal from './MoveCardModal'; // Will be created in a later step

import {
    ModalOverlay,
    ModalContent,
    MainContent,
    CloseButton,
    DueDatePopover,
    DueDateButtonContainer,
    DueDateButton,
    DueDateRemoveButton,
    PopoverCloseButton,
    Section, // Import Section
    SectionTitle, // Import SectionTitle
    StyledInput, // Import StyledInput
    FieldLabel, // Import FieldLabel (already used but good to note)
    ClickableLinkWrapper, // New: For styling the link and edit button
    LinkDisplay, // New: For the <a> tag
    EditLinkButton, // New: For the edit button
    // Cover Image Popover Styles
    CoverImagePopover,
    CoverImagePreview,
    UploadButton,
    RemoveCoverButton,
    ModalCoverImage, // Import for main cover image display
    // Direct Remove Button for Cover Image
    CoverImageWrapper,
    DirectRemoveCoverButton,
    Button, // Added for parent/subtask actions
    SidebarButton, // Added for parent/subtask actions
    // Parent Dropdown Styles
    ParentDropdownContainer,
    ParentDropdownSearchInput,
    ParentDropdownList,
    ParentDropdownItem,
    ParentDropdownHeader,
    ParentDropdownTitle,
    ParentDropdownCloseButton,
    NoResultsMessage,
    // Styles for new relations section
    RelationsSectionHeader,
    RelationsSectionTitle,
    AddRelationButton,
    SectionTitleWithAdd,
    SmallIconButton,
    RelationTypeDropdown,
    RelationTypeDropdownItem,
} from '../styles/CardModal.styles';
import GithubIcon from './icons/GithubIcon'; // Assuming GithubIcon is appropriate here or use a generic link icon
import EditIcon from './icons/EditIcon'; // Import EditIcon
import UploadIcon from './icons/UploadIcon'; // For upload button
import PlusIcon from './icons/PlusIcon'; // Import PlusIcon
import ChevronIcon from './icons/ChevronIcon'; // Import ChevronIcon
import AddSubtaskModal from './AddSubtaskModal'; // Import AddSubtaskModal
import SetParentModal from './SetParentModal'; // Import SetParentModal
import AddRelatedCardModal from './AddRelatedCardModal'; // Import AddRelatedCardModal
import AddBlockerModal from './AddBlockerModal'; // Import AddBlockerModal
import MobileCardSidebar from './card/MobileCardSidebar';
import imageCompression from 'browser-image-compression';

const CardModal = ({
    project,
    card: initialCard,
    column,
    projectId,
    onClose,
    projectMembers = {},
    allColumns,
    columnOrder,
    enableGithubLinks, // Add new prop
    onSwitchCard, // New prop for direct navigation
}) => {
    const { user } = useAuth();
    const [showAssigneePicker, setShowAssigneePicker] = useState(false);
    const [showLabelPicker, setShowLabelPicker] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);
    const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDueDatePopover, setShowDueDatePopover] = useState(false);
    const [showStartDatePopover, setShowStartDatePopover] = useState(false);
    const [showMoveCardModal, setShowMoveCardModal] = useState(false); // New state for MoveCardModal
    const [showCoverImagePopover, setShowCoverImagePopover] = useState(false); // State for cover image popover

    const [cardData, setCardData] = useState({
        ...initialCard,
        attachments: initialCard.attachments || [],
    });
    const [labels, setLabels] = useState([]);
    const [comment, setComment] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [assignee, setAssignee] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(column.id);
    const [selectedPriority, setSelectedPriority] = useState(initialCard.priority);
    const [selectedDueDate, setSelectedDueDate] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    // For GitHub URL
    const [githubUrl, setGithubUrl] = useState(initialCard.githubUrl || '');
    const [isEditingGithubUrl, setIsEditingGithubUrl] = useState(false);

    // For Card Relations Section
    const [isRelationsExpanded, setIsRelationsExpanded] = useState(false);
    const [showAddRelationDropdown, setShowAddRelationDropdown] = useState(false);
    const addRelationDropdownRef = React.useRef(null);

    // States for new modals
    const [showSetParentModal, setShowSetParentModal] = useState(false);
    const [showAddRelatedCardModal, setShowAddRelatedCardModal] = useState(false);
    const [showAddBlockerModal, setShowAddBlockerModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isMobileSectionsExpanded, setIsMobileSectionsExpanded] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    // For Cover Image
    const [coverImageBase64, setCoverImageBase64] = useState(null);
    const [isLoadingCoverImage, setIsLoadingCoverImage] = useState(false);

    // For Parent Task
    const [showParentDropdown, setShowParentDropdown] = useState(false); // Renamed
    const [parentSearchTerm, setParentSearchTerm] = useState(''); // Added

    // const [potentialParentId, setPotentialParentId] = useState(''); // Removed

    const parentDropdownRef = React.useRef(null); // For click outside
    const parentSearchInputRef = React.useRef(null); // For focus

    // State for Add Subtask Modal
    const [showAddSubtaskModal, setShowAddSubtaskModal] = useState(false);

    useEffect(() => {
        if (showDueDatePopover && cardData.dueDate) {
            setSelectedDueDate(new Date(cardData.dueDate));
        } else if (!showDueDatePopover) {
            setSelectedDueDate(null); // Reset when popover is closed
        }
    }, [showDueDatePopover, cardData.dueDate]);

    useEffect(() => {
        if (showStartDatePopover && cardData.startDate) {
            setSelectedStartDate(new Date(cardData.startDate));
        } else if (!showStartDatePopover) {
            setSelectedStartDate(null); // Reset when popover is closed
        }
    }, [showStartDatePopover, cardData.startDate]);

    useEffect(() => {
        // Only update cardData if the card ID has changed or if it's the first load
        if (!cardData.id || cardData.id !== initialCard.id) {
            setCardData({
                ...initialCard,
                checklists: initialCard.checklists || [],
                labelIds: initialCard.labelIds || [],
                activity: initialCard.activity || [],
                comments: initialCard.comments || [],
                githubUrl: initialCard.githubUrl || '' // Ensure cardData is also aware
            });
            setGithubUrl(initialCard.githubUrl || ''); // Update local state for editing
            setIsEditingGithubUrl(false); // Reset editing state when card changes
        }
    }, [initialCard.id]);

    useEffect(() => {
        const fetchCover = async () => {
            setIsLoadingCoverImage(true);
            const cacheKey = `coverImage_${initialCard.id}`;
            const cacheIdKey = `coverImageId_${initialCard.id}`;

            if (!initialCard.coverImageId) {
                // Cover image was removed (externally or locally)
                localStorage.removeItem(cacheKey);
                localStorage.removeItem(cacheIdKey);
                setCoverImageBase64(null);
                setIsLoadingCoverImage(false);
                return;
            }

            const cachedImageId = localStorage.getItem(cacheIdKey);
            const cachedImage = localStorage.getItem(cacheKey);

            if (cachedImage && cachedImageId === initialCard.coverImageId) {
                setCoverImageBase64(cachedImage);
                setIsLoadingCoverImage(false);
            } else {
                // Not cached, cache is stale, or ID mismatch, so fetch from Firebase
                try {
                    const imageDoc = await getCoverImage(initialCard.coverImageId);
                    if (imageDoc && imageDoc.base64Image) {
                        setCoverImageBase64(imageDoc.base64Image);
                        localStorage.setItem(cacheKey, imageDoc.base64Image);
                        localStorage.setItem(cacheIdKey, initialCard.coverImageId);
                    } else {
                        setCoverImageBase64(null);
                        localStorage.removeItem(cacheKey); // Remove potentially stale cache
                        localStorage.removeItem(cacheIdKey);
                    }
                } catch (error) {
                    console.error("Error fetching cover image:", error);
                    setCoverImageBase64(null);
                    localStorage.removeItem(cacheKey); // Attempt to remove cache on error too
                    localStorage.removeItem(cacheIdKey);
                } finally {
                    setIsLoadingCoverImage(false);
                }
            }
        };

        if (initialCard.id) { // Ensure initialCard.id is available
            fetchCover();
        } else {
            // Handle case where initialCard.id might not be immediately available
            // or if there's no cover image to fetch.
            setCoverImageBase64(null);
            setIsLoadingCoverImage(false);
        }
    }, [initialCard.id, initialCard.coverImageId]); // Depend on initialCard.id as well for cache keys

    useEffect(() => {
        const fetchLabels = async () => {
            try {
                const projectRef = doc(db, 'projects', projectId);
                const projectSnap = await getDoc(projectRef);
                const data = projectSnap.data();
                setLabels(data.labels || []);
            } catch (error) {
                console.error('Error fetching labels:', error);
            }
        };
        fetchLabels();
    }, [projectId]);

    useEffect(() => {
        if (initialCard.assigneeId && projectMembers[initialCard.assigneeId]) {
            setAssignee(projectMembers[initialCard.assigneeId]);
        } else {
            setAssignee(null);
        }
    }, [initialCard.assigneeId, projectMembers]);

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleGithubUrlBlur = async () => {
        const oldUrl = initialCard.githubUrl || '';
        const newUrl = githubUrl.trim();

        if (newUrl !== oldUrl) {
            if (!newUrl || isValidUrl(newUrl)) {
                let activityActionDescriptor = {};
                if (!newUrl && oldUrl) { // URL removed
                    activityActionDescriptor = { type: 'github_link_removed' };
                } else if (newUrl && !oldUrl) { // URL added
                    activityActionDescriptor = { type: 'github_link_added', details: newUrl };
                } else { // URL updated
                    activityActionDescriptor = { type: 'github_link_updated', details: newUrl };
                }
                const fullActivityLog = addActivity(user, activityActionDescriptor);
                await saveCardChanges({ githubUrl: newUrl }, fullActivityLog);
            } else {
                // Optionally, provide feedback for invalid URL
                console.warn("Invalid GitHub URL, not saving:", newUrl);
                setGithubUrl(initialCard.githubUrl || ''); // Revert to original if invalid
            }
        }
        setIsEditingGithubUrl(false);
    };

    const saveCardChanges = async (updates, action = null) => {
        const originalCardData = { ...cardData }; // Capture current cardData for potential revert
        setIsSaving(true);

        let activityToLog = null;
        if (action) {
            activityToLog = action; // Use the already formatted action object directly
        }

        // Prepare the optimistic update object
        // Start with current cardData, apply explicit updates, then add activity
        const optimisticUpdatedCard = {
            ...cardData,
            ...updates, // Apply passed updates (e.g., { githubUrl: 'new_url' })
        };

        if (activityToLog) {
            optimisticUpdatedCard.activity = [...(optimisticUpdatedCard.activity || []), activityToLog];
        }

        // Optimistically update the local state (cardData and specific states like githubUrl if needed)
        setCardData(optimisticUpdatedCard);
        if (updates.githubUrl !== undefined) {
            setGithubUrl(updates.githubUrl); // Keep githubUrl state in sync if it's being updated
        }
        // Similar updates for other fields if they have their own state variables

        try {
            const projectRef = doc(db, 'projects', projectId);

            // The payload for Firebase should be the complete, optimistically updated card object
            let firebasePayload = { ...optimisticUpdatedCard };

            // SAFEGURAD: Ensure base64 image data is NOT part of the card saved to the project
            // The actual base64 image is stored in a separate 'coverImages' collection.
            // Only 'coverImageId' should be stored in the card object within the project.
            if (firebasePayload.hasOwnProperty('coverImageBase64')) {
                // Create a new object without the coverImageBase64 property
                const { coverImageBase64, ...rest } = firebasePayload;
                firebasePayload = rest;
            }

            // Firestore does not allow 'undefined' values. Convert known nullable fields if they are undefined.
            const nullableFieldsInCard = ['priority', 'assigneeId', 'dueDate', 'parentId', 'coverImageId', 'githubUrl'];
            // Added githubUrl as it can also be 'unset' by making it empty.
            // Description is a string, usually empty string not null.
            // Labels, checklists, comments, activity, attachments are arrays, usually empty array not null.

            for (const key of nullableFieldsInCard) {
                if (firebasePayload.hasOwnProperty(key) && firebasePayload[key] === undefined) {
                    firebasePayload[key] = null;
                }
            }

            // Construct the update for the specific card within the column
            // Use selectedStatus to get the card's CURRENT column ID
            const currentColumnId = selectedStatus;
            const currentColumnData = allColumns[currentColumnId];

            if (!currentColumnData) {
                console.error("saveCardChanges: Current column data not found for ID:", currentColumnId);
                // Revert optimistic update and throw error or return false
                setCardData(originalCardData);
                // Potentially revert other specific states like githubUrl if they were part of 'updates'
                if (updates.githubUrl !== undefined) setGithubUrl(originalCardData.githubUrl || '');
                alert('Error updating card: Current column not found. Your changes were not saved.');
                setIsSaving(false); // Ensure isSaving is reset
                return false; // Indicate failure
            }

            const updatedCardsInCurrentColumn = (currentColumnData.cards || []).map(c => {
                if (c.id === originalCardData.id) { // originalCardData is derived from cardData (the card being modified)
                    return firebasePayload; // firebasePayload is the new state of cardData
                }
                return c;
            });

            await updateDoc(projectRef, {
                [`columns.${currentColumnId}.cards`]: updatedCardsInCurrentColumn,
                updatedAt: new Date().toISOString()
            });

            return true; // Indicate success
        } catch (error) {
            console.error('Error updating card:', error);
            // Revert to original state on error
            setCardData(originalCardData); // Revert main card data
            if (updates.githubUrl !== undefined) {
                 // Also revert specific state if it was part of the failed update
                setGithubUrl(originalCardData.githubUrl || '');
            }
            // TODO: Show user-facing error message
            alert('Error updating card. Your changes were not saved. Please try again.');
            return false; // Indicate failure
        } finally {
            setIsSaving(false);
        }
    };


    const handleAddAttachment = async (attachment) => {
        const updatedCard = {
            ...cardData,
            attachments: [...cardData.attachments, attachment]
        };

        await saveCardChanges(
            updatedCard,
            {
                type: 'attachment',
                details: `added attachment "${attachment.name}"`
            }
        );
    };

    const handleDeleteAttachment = async (attachmentId) => {
        const updatedCard = {
            ...cardData,
            attachments: cardData.attachments.filter(a => a.id !== attachmentId)
        };

        await saveCardChanges(
            updatedCard,
            {
                type: 'attachment',
                details: 'removed an attachment'
            }
        );
    };

    const handleStatusChange = async (newColumnId) => {
        if (newColumnId === selectedStatus) { // Check against selectedStatus for consistency
            setShowStatusDropdown(false);
            return;
        }

        const originalStatus = selectedStatus;
        const originalCardData = { ...cardData };

        const sourceColumn = allColumns[originalStatus]; // Use originalStatus to get the correct source
        const targetColumn = allColumns[newColumnId];

        if (!sourceColumn || !targetColumn) {
            console.error("Source or target column not found for status change.");
            return;
        }

        // Optimistic UI updates
        setSelectedStatus(newColumnId);
        setShowStatusDropdown(false);

        const newActivity = addActivity(user, {
            type: 'status_change',
            from: sourceColumn.title,
            to: targetColumn.title,
        });

        const optimisticallyUpdatedCardData = {
            ...cardData,
            activity: [...(cardData.activity || []), newActivity]
        };

        // New logic to handle due date completion
        if (targetColumn.title === 'Closed' && optimisticallyUpdatedCardData.dueDate) {
            optimisticallyUpdatedCardData.isDueDateCompleted = true;
        } else if (sourceColumn.title === 'Closed' && optimisticallyUpdatedCardData.dueDate) {
            optimisticallyUpdatedCardData.isDueDateCompleted = false;
        }

        setCardData(optimisticallyUpdatedCardData);

        try {
            const projectRef = doc(db, 'projects', projectId);

            // Prepare the card data that will be moved. It should include the new activity.
            const cardToMove = { ...optimisticallyUpdatedCardData };
            // Ensure cardToMove does not have fields that are only meant for local state if any.
            // For this case, optimisticallyUpdatedCardData should be fine.

            const sourceCards = (sourceColumn.cards || []).filter(c => c.id !== cardData.id);

            // Ensure targetCards is an array even if targetColumn.cards is undefined
            const currentTargetCards = targetColumn.cards || [];
            const targetCards = [...currentTargetCards, cardToMove];

            const updates = {
                [`columns.${sourceColumn.id}.cards`]: sourceCards,
                [`columns.${targetColumn.id}.cards`]: targetCards,
                updatedAt: new Date().toISOString()
            };

            await updateDoc(projectRef, updates);
            // Success: Optimistic updates are now confirmed.
            // The `column` prop itself is not updated here, that would require lifting state
            // or re-fetching data at a higher level. The modal's internal `selectedStatus`
            // and `cardData` (with activity) are now current.

        } catch (error) {
            console.error('Error changing status:', error);
            // Revert optimistic updates
            setSelectedStatus(originalStatus);
            setCardData(originalCardData);
            alert('Error changing status. Your changes were not saved. Please try again.');
            // Note: If the dropdown was closed, it remains closed. This might be acceptable.
        }
    };

    const handleNextStatus = () => {
        if (!columnOrder || columnOrder.length === 0) return;

        const currentIndex = columnOrder.indexOf(selectedStatus);
        if (currentIndex === -1) return; // Should not happen if selectedStatus is always valid

        const nextIndex = (currentIndex + 1) % columnOrder.length;
        const nextColumnId = columnOrder[nextIndex];

        if (nextColumnId) {
            handleStatusChange(nextColumnId); // handleStatusChange will optimistically update selectedStatus
        }
    };

    const handlePriorityChange = async (newPriority) => {
        const originalPriority = selectedPriority;
        // Optimistically update UI
        setSelectedPriority(newPriority);
        setShowPriorityDropdown(false); // Close dropdown immediately

        const success = await saveCardChanges(
            { priority: newPriority },
            {
                type: 'priority',
                from: originalPriority, // Use originalPriority for the log
                to: newPriority
                // The 'details' field is removed here, addActivity will construct the message
            }
        );

        if (!success) {
            // Revert optimistic UI update if save failed
            setSelectedPriority(originalPriority);
            // Optionally, re-open dropdown or show a specific error for priority change
        }
    };

    // TODO: Refactor: This card deletion logic is similar to CardQuickActionsMenu.js.
    // Consider extracting to a utility function in projectHandlers.js or firebaseUtils.js, e.g., deleteCardFromColumn(projectId, columnId, cardId).
    const handleDeleteCard = async () => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            const updatedCards = column.cards.filter(c => c.id !== cardData.id);

            await updateDoc(projectRef, {
                [`columns.${column.id}.cards`]: updatedCards,
                updatedAt: new Date().toISOString()
            });

            onClose();
        } catch (error) {
            console.error('Error deleting card:', error);
        }
    };

    // TODO: Refactor: Updating project's main labels array could be a utility in projectHandlers.js or firebaseUtils.js.
    // e.g., updateProjectLabels(projectId, updatedLabelsArray). Similar logic in CardQuickActionsMenu.js.
    const handleCreateLabel = async (newLabel) => {
        try {
            const projectRef = doc(db, 'projects', projectId);
            let updatedLabels;

            if (newLabel.deleted) {
                updatedLabels = labels.filter(l => l.id !== newLabel.id);
            } else {
                const existingLabel = labels.find(l => l.id === newLabel.id);
                updatedLabels = existingLabel
                    ? labels.map(l => l.id === newLabel.id ? newLabel : l)
                    : [...labels, newLabel];
            }

            await updateDoc(projectRef, { labels: updatedLabels });
            setLabels(updatedLabels);
        } catch (error) {
            console.error('Error updating labels:', error);
        }
    };

    // TODO: Refactor: Card update logic (for labelIds) is similar to CardQuickActionsMenu.js and could use a shared utility.
    // The saveCardChanges function here centralizes updates, but the pattern of preparing 'updates' and 'activity' is common.
    const handleToggleLabel = (labelId) => {
        const cardLabels = cardData.labelIds || [];
        const hasLabel = cardLabels.includes(labelId);
        const updatedLabelIds = hasLabel
            ? cardLabels.filter(id => id !== labelId)
            : [...cardLabels, labelId];

        const label = labels.find(l => l.id === labelId);
        const activityAction = addActivity(user, {
            type: 'label',
            from: label.title,
            details: hasLabel ? 'removed' : 'added'
        });
        saveCardChanges(
            { labelIds: updatedLabelIds },
            activityAction
        );
    };

    const handleAddChecklist = () => {
        const newChecklist = {
            id: `checklist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            title: 'Checklist',
            items: []
        };

        const updatedChecklists = [...(cardData.checklists || []), newChecklist];
        const activity = addActivity(user, {
            type: 'checklist',
            from: newChecklist.title,
            details: 'added'
        });
        saveCardChanges(
            { checklists: updatedChecklists },
            activity
        );
    };

    const handleDeleteChecklist = (checklistId) => {
        const checklistToDelete = cardData.checklists.find(cl => cl.id === checklistId);
        if (!checklistToDelete) {
            console.error("Checklist not found for deletion:", checklistId);
            return;
        }
        const updatedChecklists = cardData.checklists.filter(cl => cl.id !== checklistId);
        const activity = addActivity(user, {
            type: 'checklist',
            from: checklistToDelete.title,
            details: 'deleted'
        });
        saveCardChanges(
            { checklists: updatedChecklists },
            activity
        );
    };

    const handleUpdateChecklistItem = async (checklistId, itemId, newText) => {
        const checklist = cardData.checklists.find(cl => cl.id === checklistId);
        const item = checklist?.items.find(it => it.id === itemId);

        if (!checklist || !item) {
            console.error("Checklist or item not found for update");
            return;
        }

        const oldText = item.text;

        const updatedChecklists = cardData.checklists.map(cl =>
            cl.id === checklistId
                ? {
                    ...cl,
                    items: cl.items.map(i =>
                        i.id === itemId
                            ? { ...i, text: newText }
                            : i
                    )
                }
                : cl
        );

        let activity = null;
        if (oldText !== newText) {
            activity = addActivity(user, {
                type: 'checklist_item_updated',
                details: `updated item "${oldText}" to "${newText}" in checklist "${checklist.title}"`
            });
        }

        await saveCardChanges({ checklists: updatedChecklists }, activity);
    };

    const handleDeleteChecklistItem = async (checklistId, itemId) => {
        const checklist = cardData.checklists.find(cl => cl.id === checklistId);
        const itemToDelete = checklist?.items.find(it => it.id === itemId);

        if (!checklist || !itemToDelete) {
            console.error("Checklist or item not found for deletion");
            return;
        }

        const updatedChecklists = cardData.checklists.map(cl =>
            cl.id === checklistId
                ? {
                    ...cl,
                    items: cl.items.filter(item => item.id !== itemId)
                }
                : cl
        );

        const activity = addActivity(user, {
            type: 'checklist_item_deleted',
            details: `deleted item "${itemToDelete.text}" from checklist "${checklist.title}"`
        });

        await saveCardChanges({ checklists: updatedChecklists }, activity);
    };

    const handleAddChecklistItem = async (checklistId, text) => {
        if (!text.trim()) return;

        const newItem = {
            id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: text.trim(),
            completed: false
        };

        const checklist = cardData.checklists.find(cl => cl.id === checklistId);
        if (!checklist) {
            console.error("Checklist not found for adding item");
            return;
        }

        const updatedChecklists = cardData.checklists.map(cl =>
            cl.id === checklistId
                ? {
                    ...cl,
                    items: [...checklist.items, newItem]
                }
                : cl
        );

        const activity = addActivity(user, {
            type: 'checklist_item_added',
            details: `added item "${newItem.text}" to checklist "${checklist.title}"`
        });

        await saveCardChanges({ checklists: updatedChecklists }, activity);
    };

    // TODO: Refactor: Card update logic (for archive status) is similar to CardQuickActionsMenu.js and could use a shared utility.
    const handleArchiveCard = async () => {
        const updatedCard = {
            ...cardData,
            archived: !cardData.archived,
            archivedAt: cardData.archived ? null : new Date().toISOString()
        };

        await saveCardChanges(
            updatedCard,
            {
                type: 'archive',
                details: `${user.displayName} ${cardData.archived ? 'sent card to board' : 'archived card'}`
            }
        );
    };

    // TODO: Refactor: Adding a new card to a column is similar to CardQuickActionsMenu.js and could use a shared utility.
    const handleDuplicateCard = async () => {
        try {
            const newCard = duplicateCard(cardData);
            const projectRef = doc(db, 'projects', projectId);
            const currentCards = column.cards || [];
            const updatedCards = [...currentCards, newCard];

            await updateDoc(projectRef, {
                [`columns.${column.id}.cards`]: updatedCards,
                updatedAt: new Date().toISOString()
            });

            const newActivity = addActivity(user, {
                type: 'duplicate',
                details: `duplicated this card as "${newCard.title}"`
            });
            setCardData({
                ...newCard,
                activity: [newActivity]
            });
        } catch (error) {
            console.error('Error duplicating card:', error);
        }
    };

    // TODO: Refactor: Card update logic (for assigneeId) is similar to CardQuickActionsMenu.js and could use a shared utility.
    const handleAssign = async (userId) => {
        const originalAssignee = assignee;
        const newAssigneeData = projectMembers[userId];

        // Optimistically update UI
        setAssignee(newAssigneeData);
        setShowAssigneePicker(false); // Close picker immediately

        const success = await saveCardChanges(
            { assigneeId: userId },
            {
                type: 'assign',
                details: `${user.displayName} assigned this card to ${newAssigneeData?.displayName || newAssigneeData?.email || 'Unknown User'}`
            }
        );

        if (!success) {
            // Revert optimistic UI update if save failed
            setAssignee(originalAssignee);
            // Optionally, re-open picker or show a specific error
        }
    };

    // TODO: Refactor: Card update logic (for assigneeId to null) is similar to CardQuickActionsMenu.js and could use a shared utility.
    const handleUnassign = async () => {
        if (!assignee) return; // No one to unassign

        const originalAssignee = assignee; // For activity log
        setIsSaving(true); // Indicate saving process

        // Optimistically update UI
        setAssignee(null);
        setShowAssigneePicker(false); // Close picker

        const success = await saveCardChanges(
            { assigneeId: null }, // Send null to clear the assigneeId
            {
                type: 'unassign', // Custom type for unassigning
                details: `${user.displayName} unassigned ${originalAssignee?.displayName || originalAssignee?.email || 'User'} from this card`
            }
        );

        if (!success) {
            // Revert optimistic UI update if save failed
            setAssignee(originalAssignee); // Restore original assignee
            // Optionally, show an error message
            alert('Error unassigning user. Please try again.');
        }
        // setIsSaving(false) will be handled by saveCardChanges
    };

    const toggleChecklistItem = async (checklistId, itemId) => {
        let rawActivityAction = null;
        const checklist = cardData.checklists.find(cl => cl.id === checklistId);
        const item = checklist?.items.find(it => it.id === itemId);

        if (user && checklist && item) {
            if (!item.completed) {
                rawActivityAction = {
                    type: 'checklist_item_completed',
                    details: `marked "${item.text}" as completed in "${checklist.title}"`
                };
            } else {
                rawActivityAction = {
                    type: 'checklist_item_incomplete',
                    details: `marked "${item.text}" as incomplete in "${checklist.title}"`
                };
            }
        }

        const updatedChecklists = cardData.checklists.map(cl =>
            cl.id === checklistId
                ? {
                    ...cl,
                    items: cl.items.map(i =>
                        i.id === itemId
                            ? { ...i, completed: !i.completed }
                            : i
                    )
                }
                : cl
        );

        if (rawActivityAction) {
            const activity = addActivity(user, rawActivityAction);
            await saveCardChanges({ checklists: updatedChecklists }, activity);
        } else {
            await saveCardChanges({ checklists: updatedChecklists });
        }
    };

    const handleAddComment = async () => {
        if (!comment.trim() || isSaving) return;

        const newComment = {
            id: `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: comment.trim(),
            createdAt: new Date().toISOString(),
            userName: user.displayName,
            userId: user.uid // Add userId to comments
        };

        await saveCardChanges({
            comments: [...(cardData.comments || []), newComment]
        });
        setComment('');
    };

    const handleUpdateComment = async (commentId, newText) => {
        if (!newText.trim() || isSaving) return;

        const updatedComments = cardData.comments.map(c => {
            if (c.id === commentId) {
                const previousVersion = {
                    text: c.text,
                    editedAt: c.editedAt || c.createdAt, // Use previous editedAt or createdAt if first edit
                };
                const history = c.history ? [...c.history, previousVersion] : [previousVersion];
                return { ...c, text: newText.trim(), editedAt: new Date().toISOString(), history };
            }
            return c;
        });

        await saveCardChanges({
            comments: updatedComments
        });
    };

    const onDragEnd = async (result) => {
        const { destination, source, type } = result;

        if (!destination || (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        )) {
            return;
        }

        if (type === 'CARD_CHECKLIST_ITEM') {
            const sourceChecklistId = source.droppableId.replace('checklist-', '');
            const destChecklistId = destination.droppableId.replace('checklist-', '');

            const updatedChecklists = [...cardData.checklists];
            const sourceChecklist = updatedChecklists.find(cl => cl.id === sourceChecklistId);
            const destChecklist = updatedChecklists.find(cl => cl.id === destChecklistId);

            if (!sourceChecklist || !destChecklist) return;

            const [movedItem] = sourceChecklist.items.splice(source.index, 1);
            destChecklist.items.splice(destination.index, 0, movedItem);

            await saveCardChanges({ checklists: updatedChecklists });
        }
    };

    const toggleDueDatePopover = () => {
        setShowDueDatePopover(!showDueDatePopover);
    };

    const toggleStartDatePopover = () => {
        setShowStartDatePopover(!showStartDatePopover);
    };

    const handleRawDueDateChange = (date) => {
        setSelectedDueDate(date);
    };

    const handleRawStartDateChange = (date) => {
        setSelectedStartDate(date);
    };

    // TODO: Refactor: Card update logic (for dueDate) is similar to CardQuickActionsMenu.js and could use a shared utility.
    const saveDueDate = async () => {
        if (!selectedDueDate) return;

        const newDueDate = selectedDueDate.toISOString();
        await saveCardChanges(
            { dueDate: newDueDate, isDueDateCompleted: false },
            {
                type: 'due_date_set',
                details: `set Due Time to ${selectedDueDate.toLocaleString()}`
            }
        );
        toggleDueDatePopover();
    };

    const saveStartDate = async () => {
        if (!selectedStartDate) return;

        const newStartDate = selectedStartDate.toISOString();
        await saveCardChanges(
            { startDate: newStartDate },
            {
                type: 'start_date_set',
                details: `set Start Time to ${selectedStartDate.toLocaleString()}`
            }
        );
        toggleStartDatePopover();
    };

    const clearStartDate = async () => {
        await saveCardChanges(
            { startDate: null },
            { type: 'start_date_removed' }
        );
        toggleStartDatePopover();
    };

    // TODO: Refactor: Card update logic (for clearing dueDate) is similar to CardQuickActionsMenu.js and could use a shared utility.
    const clearDueDate = async () => {
        await saveCardChanges(
            { dueDate: null, isDueDateCompleted: false },
            { type: 'due_date_removed' }
        );
        toggleDueDatePopover();
    };

    // Placeholder for actual workspaceId - this needs to be fetched or passed
    // For now, this will prevent the app from crashing if MoveCardModal is rendered.
    // State to hold the fetched workspaceId
    const [currentWorkspaceId, setCurrentWorkspaceId] = useState('');
    const [isLoadingWorkspaceId, setIsLoadingWorkspaceId] = useState(false);
    const fileInputRef = React.useRef(null); // Ref for the hidden file input

    // --- Parent Task Logic ---
    const findCardById = (cardIdToFind) => {
        if (!allColumns || !cardIdToFind) return null;
        for (const columnId in allColumns) {
            const column = allColumns[columnId];
            if (column.cards) {
                const foundCard = column.cards.find(c => c.id === cardIdToFind);
                if (foundCard) return foundCard;
            }
        }
        return null;
    };

    const parentCard = cardData.parentId ? findCardById(cardData.parentId) : null;

    const getAllDescendantIds = (cardId, currentAllColumns) => {
        let descendants = new Set();
        if (!cardId || !currentAllColumns) return descendants;

        let queue = [cardId];
        const visitedInTraversal = new Set(); // To avoid processing the same card multiple times if loops somehow exist or for performance

        while (queue.length > 0) {
            const currentCardId = queue.shift();
            if (visitedInTraversal.has(currentCardId) && currentCardId !== cardId) continue; // cardId is the root, can be re-added to queue by mistake if not handled
            visitedInTraversal.add(currentCardId);

            for (const colId in currentAllColumns) {
                const column = currentAllColumns[colId];
                if (column.cards) {
                    for (const card of column.cards) {
                        if (card.parentId === currentCardId) {
                            if (!descendants.has(card.id)) { // Check before adding to prevent infinite loop if data is bad
                                descendants.add(card.id);
                                queue.push(card.id);
                            }
                        }
                    }
                }
            }
        }
        return descendants;
    };

    const isCircularDependency = (childCardId, prospectiveParentId) => {
        if (!childCardId || !prospectiveParentId) return false; // Should not happen with valid inputs
        if (childCardId === prospectiveParentId) return true;

        const descendantsOfChild = getAllDescendantIds(childCardId, allColumns);
        if (descendantsOfChild.has(prospectiveParentId)) {
            // console.warn(`Circular dependency detected: ${prospectiveParentId} is a descendant of ${childCardId}`);
            return true;
        }
        return false;
    };

    // Modified to accept selectedCardId, further changes in Step 4
    const handleSetParent = async (selectedCardId) => {
        if (!selectedCardId || selectedCardId.trim() === '') {
            alert("No parent card selected or invalid ID."); // Should not happen with dropdown
            return;
        }
        if (selectedCardId === cardData.id) {
            alert("A card cannot be its own parent."); // Should be caught by dropdown filtering
            return;
        }

        if (isCircularDependency(cardData.id, selectedCardId)) {
            alert("Setting this card as a parent would create a circular dependency."); // Should be caught by dropdown filtering
            return;
        }

        const targetParent = findCardById(selectedCardId);
        if (!targetParent) {
            alert("Parent card not found with the provided ID."); // Should not happen if ID comes from valid list
            return;
        }

        // If all checks pass:
        const success = await saveCardChanges(
            { parentId: selectedCardId },
            addActivity(user, {
                type: 'parent_set',
                details: `set parent to "${targetParent.title}" (ID: ${targetParent.id})`
            })
        );

        if (success) {
            // setPotentialParentId(''); // Removed
            setShowParentDropdown(false);
            setParentSearchTerm(''); // Clear search term
        } else {
            // Error already handled by saveCardChanges (alert)
        }
    };

    const handleRemoveParent = async () => {
        if (cardData.parentId) {
            const oldParentCard = findCardById(cardData.parentId);
            await saveCardChanges(
                { parentId: null },
                addActivity(user, {
                    type: 'parent_removed', // This type will be added to cardUtils
                    details: `removed parent "${oldParentCard ? oldParentCard.title : cardData.parentId}"`
                })
            );
            // No need to change potentialParentId or showParentSelector here
        }
    };
    // --- End Parent Task Logic ---

    // --- Subtasks Logic ---
    const [subtasks, setSubtasks] = useState([]);

    useEffect(() => {
        if (cardData && cardData.id && allColumns) {
            const currentSubtasks = [];
            for (const columnId in allColumns) {
                const column = allColumns[columnId];
                if (column.cards) {
                    column.cards.forEach(c => {
                        // Ensure card 'c' itself is not null or undefined
                        if (c && c.parentId === cardData.id) {
                            currentSubtasks.push(c);
                        }
                    });
                }
            }
            // Optional: Sort subtasks, e.g., by creation date or title
            // currentSubtasks.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
            setSubtasks(currentSubtasks);
        } else {
            setSubtasks([]);
        }
    }, [cardData, cardData.id, allColumns]); // Added cardData.id to dependencies for clarity

    // Effect for focusing search input when dropdown opens
    useEffect(() => {
        if (showParentDropdown && parentSearchInputRef.current) {
            parentSearchInputRef.current.focus();
        }
    }, [showParentDropdown]);

    // Effect for handling click outside of parent dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target)) {
                // Clicked outside
                // Check if the click was on the "Set Parent" button itself to avoid immediate re-open.
                // This requires a ref on the "Set Parent" button or a more complex check.
                // For now, a simple close. If the "Set Parent" button is clicked, it will toggle it open again.
                setShowParentDropdown(false);
                setParentSearchTerm('');
            }
        };

        if (showParentDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showParentDropdown]); // Rerun when showParentDropdown changes

    // --- Modal Open/Close Handlers ---
    const handleOpenAddSubtaskModal = () => setShowAddSubtaskModal(true);
    const handleCloseAddSubtaskModal = () => setShowAddSubtaskModal(false);
    const handleOpenSetParentModal = () => setShowSetParentModal(true);
    const handleCloseSetParentModal = () => setShowSetParentModal(false);
    const handleOpenAddRelatedCardModal = () => setShowAddRelatedCardModal(true);
    const handleCloseAddRelatedCardModal = () => setShowAddRelatedCardModal(false);
    const handleOpenAddBlockerModal = () => setShowAddBlockerModal(true);
    const handleCloseAddBlockerModal = () => setShowAddBlockerModal(false);

    // --- Callbacks for after modal actions ---
    const handleSubtaskAddedInModal = async (newSubtaskObject) => {
        if (user) {
            const parentActivityDetails = `added subtask "${newSubtaskObject.title}"`;
            const newActivityForParent = addActivity(user, { type: 'child_task_added', details: parentActivityDetails });
            await saveCardChanges({ activity: [...(cardData.activity || []), newActivityForParent] });
            // UI will refresh due to allColumns dependency update from useProjectData
        }
    };

    const handleParentSetInModal = async (updatedCardWithNewParent) => {
        // updatedCardWithNewParent already has its parentId and activity updated by SetParentModal
        // We just need to ensure the local cardData is refreshed if needed,
        // but saveCardChanges was already called within SetParentModal effectively.
        // Forcing a refresh of cardData based on the result might be good for local consistency immediately.
        setCardData(prev => ({ ...prev, ...updatedCardWithNewParent }));
        // The activity log for "parent_set" is handled within SetParentModal.
        // No further specific activity logging needed on the parent card here beyond what SetParentModal did.
    };

    const handleRelatedCardAddedInModal = async (updatedCurrentCard, /* updatedRelatedCard */) => {
        // Both cards (current and the one it's related to) have been updated in Firestore by AddRelatedCardModal.
        // Activity logs are also added to both cards within that modal.
        // We should update the local cardData for the current card.
        setCardData(prev => ({ ...prev, ...updatedCurrentCard }));
    };

    const handleBlockerAddedInModal = async (updatedCurrentCard, /* updatedBlockerCard */) => {
        // Similar to related cards, both cards (current and the blocker) are updated in Firestore
        // by AddBlockerModal, including their activity logs.
        // Update local cardData for the current card.
        setCardData(prev => ({ ...prev, ...updatedCurrentCard }));
    };
    // --- End Subtasks Logic ---

    // --- Related Cards Logic ---
    const [displayedRelatedCards, setDisplayedRelatedCards] = useState([]);

    useEffect(() => {
        if (!cardData || !allColumns) {
            setDisplayedRelatedCards([]);
            return;
        }

        const siblings = [];
        if (cardData.parentId) {
            // Find the parent card to iterate its column for siblings
            const parent = findCardById(cardData.parentId);
            if (parent) {
                // Iterate all columns to find cards that share the same parentId
                for (const columnId in allColumns) {
                    const column = allColumns[columnId];
                    if (column.cards) {
                        column.cards.forEach(c => {
                            if (c.parentId === cardData.parentId && c.id !== cardData.id) {
                                siblings.push(c);
                            }
                        });
                    }
                }
            }
        } else {
            // If the current card has no parent, it might be a parent itself.
            // Its children (subtasks) are already listed.
            // For "Related cards", we can find other top-level cards (cards with no parentId)
            // This part of sibling logic might need refinement based on exact requirements for top-level cards.
            // For now, focusing on siblings if a parent exists.
            // Alternative: list other top-level cards if current card is top-level.
             for (const columnId in allColumns) {
                const column = allColumns[columnId];
                if (column.cards) {
                    column.cards.forEach(c => {
                        if (!c.parentId && c.id !== cardData.id) { // Other top-level cards
                            // Check if this definition of "sibling" for top-level cards is desired.
                            // siblings.push(c); // Temporarily disable this to avoid clutter if not explicitly asked for.
                        }
                    });
                }
            }
        }

        const manuallyRelated = [];
        if (cardData.manuallyRelatedCardIds && cardData.manuallyRelatedCardIds.length > 0) {
            cardData.manuallyRelatedCardIds.forEach(relatedId => {
                const relatedCard = findCardById(relatedId);
                if (relatedCard) {
                    manuallyRelated.push(relatedCard);
                }
            });
        }

        // Combine and remove duplicates
        const combined = [...siblings, ...manuallyRelated];
        const uniqueRelatedCards = Array.from(new Set(combined.map(c => c.id)))
            .map(id => combined.find(c => c.id === id));

        setDisplayedRelatedCards(uniqueRelatedCards);

    }, [cardData, allColumns, findCardById]); // findCardById is stable if allColumns is

    const handleAddRelatedCard = () => {
        // TODO: Implement functionality to open a search/dropdown for adding related cards
        console.log("Add Related Card button clicked - functionality to be implemented.");
        alert("Functionality to add related card (search/dropdown) is not yet implemented.");
    };
    // --- End Related Cards Logic ---

    // --- Blocked/Blocking Cards Logic ---
    const [blockedByCards, setBlockedByCards] = useState([]);
    const [blockingCards, setBlockingCards] = useState([]);
    const [showAddBlockerDropdown, setShowAddBlockerDropdown] = useState(false);
    const [blockerSearchTerm, setBlockerSearchTerm] = useState('');
    const addBlockerDropdownRef = React.useRef(null);
    const blockerSearchInputRef = React.useRef(null);

    useEffect(() => {
        if (!cardData || !allColumns) {
            setBlockedByCards([]);
            setBlockingCards([]);
            return;
        }

        // Populate Blocked By list
        const currentBlockedBy = [];
        if (cardData.blockedByCardIds && cardData.blockedByCardIds.length > 0) {
            cardData.blockedByCardIds.forEach(blockerId => {
                const blockerCard = findCardById(blockerId);
                if (blockerCard) {
                    currentBlockedBy.push(blockerCard);
                }
            });
        }
        setBlockedByCards(currentBlockedBy);

        // Populate Blocking list (derived)
        const currentBlocking = [];
        if (cardData && cardData.id) {
            for (const columnId in allColumns) {
                const column = allColumns[columnId];
                if (column.cards) {
                    column.cards.forEach(c => {
                        if (c.blockedByCardIds && c.blockedByCardIds.includes(cardData.id)) {
                            currentBlocking.push(c);
                        }
                    });
                }
            }
        }
        setBlockingCards(currentBlocking);

    }, [cardData, allColumns, findCardById]);


    // TODO: Implement handleAddBlocker similar to handleAddRelatedCard
    // const handleAddBlocker = () => { // Renamed, was ()_ =>
    //     setShowAddBlockerDropdown(true); // This will just show the dropdown
    //     setBlockerSearchTerm('');
    // };

    // Compute available cards for "Add Blocker" dropdown
    const availableForBlocking = React.useMemo(() => {
        if (!allColumns || !cardData || !cardData.id) return [];

        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }

        const currentBlockedByIds = new Set((cardData.blockedByCardIds || []));

        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false;
            // A card cannot block itself.
            // A card cannot be added if it's already blocking the current card.
            return card.id !== cardData.id && !currentBlockedByIds.has(card.id);
        });
    }, [allColumns, cardData]);

    const filteredBlockerCandidates = React.useMemo(() => {
        if (!blockerSearchTerm) {
            return availableForBlocking;
        }
        return availableForBlocking.filter(card =>
            card.title.toLowerCase().includes(blockerSearchTerm.toLowerCase())
        );
    }, [availableForBlocking, blockerSearchTerm]);

    // Effect for focusing search input when "Add Blocker" dropdown opens
    useEffect(() => {
        if (showAddBlockerDropdown && blockerSearchInputRef.current) {
            blockerSearchInputRef.current.focus();
        }
    }, [showAddBlockerDropdown]);

    // Effect for handling click outside of "Add Blocker" dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (addBlockerDropdownRef.current && !addBlockerDropdownRef.current.contains(event.target)) {
                setShowAddBlockerDropdown(false);
                setBlockerSearchTerm('');
            }
        };

        if (showAddBlockerDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAddBlockerDropdown]);

    // Effect for handling click outside of "Add Relation" dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (addRelationDropdownRef.current && !addRelationDropdownRef.current.contains(event.target)) {
                setShowAddRelationDropdown(false);
            }
        };

        if (showAddRelationDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAddRelationDropdown]);

     // TODO: Implement handleSaveBlocker similar to handleSaveManualRelatedCard
    const handleSaveBlocker = async (selectedBlockerCardId) => {
        if (!selectedBlockerCardId || selectedBlockerCardId === cardData.id) {
            alert("Invalid selection for blocker card.");
            return;
        }

        const currentCardId = cardData.id;
        const currentBlockedByIds = cardData.blockedByCardIds || [];

        if (currentBlockedByIds.includes(selectedBlockerCardId)) {
            setShowAddBlockerDropdown(false); // Already blocked by this card
            setBlockerSearchTerm('');
            return;
        }

        const blockerCardObject = findCardById(selectedBlockerCardId);
        if (!blockerCardObject) {
            alert("Selected blocker card not found.");
            return;
        }

        setIsSaving(true);
        try {
            const projectRef = doc(db, 'projects', projectId);

            // --- Update current card (the one being blocked) ---
            const newBlockedByForCurrent = [...currentBlockedByIds, selectedBlockerCardId];
            const activityDetailsCurrent = `marked as blocked by card "${blockerCardObject.title}"`;
            const newActivityForCurrent = addActivity(user, { type: 'card_blocked_by', details: activityDetailsCurrent });
            const updatedActivityForCurrent = [...(cardData.activity || []), newActivityForCurrent];

            const currentCardColumnId = selectedStatus;
            const currentCardCol = allColumns[currentCardColumnId];
            if (!currentCardCol) throw new Error("Current card's column not found");

            const updatedCurrentCardListInCol = currentCardCol.cards.map(c =>
                c.id === currentCardId ? {
                    ...c,
                    blockedByCardIds: newBlockedByForCurrent,
                    activity: updatedActivityForCurrent
                } : c
            );

            // --- Update the blocker card (to add activity log) ---
            const activityDetailsBlocker = `is now blocking card "${cardData.title}"`;
            const newActivityForBlocker = addActivity(user, { type: 'card_is_blocking', details: activityDetailsBlocker });
            const baseActivityBlocker = Array.isArray(blockerCardObject.activity) ? blockerCardObject.activity : [];
            const updatedActivityForBlocker = [...baseActivityBlocker, newActivityForBlocker];

            let blockerCardActualColumnId;
            let blockerCardActualColumnData;
            for (const colId in allColumns) {
                const foundCardInLoop = allColumns[colId].cards.find(c => c.id === selectedBlockerCardId);
                if (foundCardInLoop) {
                    blockerCardActualColumnId = colId;
                    blockerCardActualColumnData = allColumns[colId];
                    break;
                }
            }
            if (!blockerCardActualColumnData) throw new Error("Blocker card's column data not found");

            const updatedBlockerCardListInCol = blockerCardActualColumnData.cards.map(c =>
                c.id === selectedBlockerCardId ? {
                    ...c,
                    activity: updatedActivityForBlocker // Only activity changes on the blocker card itself
                } : c
            );

            // --- Prepare Firestore updates ---
            const firestoreUpdates = { updatedAt: new Date().toISOString() };

            if (currentCardColumnId !== blockerCardActualColumnId) {
                firestoreUpdates[`columns.${currentCardColumnId}.cards`] = updatedCurrentCardListInCol;
                firestoreUpdates[`columns.${blockerCardActualColumnId}.cards`] = updatedBlockerCardListInCol;
            } else {
                // Both cards in the same column. Merge updates.
                let finalCardsForSharedColumn = updatedCurrentCardListInCol.map(c => {
                    if (c.id === selectedBlockerCardId) { // This is the blocker card
                        return {
                            ...c,
                            activity: updatedActivityForBlocker
                        };
                    }
                    return c; // This is either the (already updated) current card or some other card
                });
                firestoreUpdates[`columns.${currentCardColumnId}.cards`] = finalCardsForSharedColumn;
            }

            await updateDoc(projectRef, firestoreUpdates);

            // Optimistically update current card's local state
            setCardData(prev => ({
                ...prev,
                blockedByCardIds: newBlockedByForCurrent,
                activity: updatedActivityForCurrent
            }));

            setShowAddBlockerDropdown(false);
            setBlockerSearchTerm('');

        } catch (error) {
            console.error("Error saving blocker card:", error);
            alert("Failed to add blocker. " + error.message);
        } finally {
            setIsSaving(false);
        }
    };
    // TODO: Implement handleRemoveBlocker
    const handleRemoveBlocker = async (blockerCardIdToRemove) => {
        if (!blockerCardIdToRemove) return;

        const currentCardId = cardData.id;
        const currentBlockedByIds = cardData.blockedByCardIds || [];

        if (!currentBlockedByIds.includes(blockerCardIdToRemove)) {
            console.warn("Attempted to remove a blocker not in the list.");
            return; // Not currently blocked by this card
        }

        const removedBlockerCardObject = findCardById(blockerCardIdToRemove);
        if (!removedBlockerCardObject) {
            alert("Blocker card to remove not found. Cannot update relation.");
            // Still proceed to remove the ID if it exists locally, to clean up.
        }

        setIsSaving(true);
        try {
            const projectRef = doc(db, 'projects', projectId);

            // --- Update current card (the one that was being blocked) ---
            const newBlockedByForCurrent = currentBlockedByIds.filter(id => id !== blockerCardIdToRemove);
            const activityDetailsCurrent = `no longer blocked by card "${removedBlockerCardObject ? removedBlockerCardObject.title : blockerCardIdToRemove}"`;
            const newActivityForCurrent = addActivity(user, { type: 'card_unblocked_by', details: activityDetailsCurrent });
            const updatedActivityForCurrent = [...(cardData.activity || []), newActivityForCurrent];

            const currentCardColumnId = selectedStatus;
            const currentCardCol = allColumns[currentCardColumnId];
            if (!currentCardCol) throw new Error("Current card's column not found");

            const updatedCurrentCardListInCol = currentCardCol.cards.map(c =>
                c.id === currentCardId ? {
                    ...c,
                    blockedByCardIds: newBlockedByForCurrent,
                    activity: updatedActivityForCurrent
                } : c
            );

            // --- Update the card that was the blocker (to add activity log) ---
            let firestoreUpdates = {
                updatedAt: new Date().toISOString(),
                [`columns.${currentCardColumnId}.cards`]: updatedCurrentCardListInCol
            };

            if (removedBlockerCardObject) { // Only if we found the card to update its activity
                const activityDetailsExBlocker = `no longer blocking card "${cardData.title}"`;
                const newActivityForExBlocker = addActivity(user, { type: 'card_no_longer_blocking', details: activityDetailsExBlocker });
                const baseActivityExBlocker = Array.isArray(removedBlockerCardObject.activity) ? removedBlockerCardObject.activity : [];
                const updatedActivityForExBlocker = [...baseActivityExBlocker, newActivityForExBlocker];

                let exBlockerCardActualColumnId;
                let exBlockerCardActualColumnData;
                for (const colId in allColumns) {
                    const foundCardInLoop = allColumns[colId].cards.find(c => c.id === blockerCardIdToRemove);
                    if (foundCardInLoop) {
                        exBlockerCardActualColumnId = colId;
                        exBlockerCardActualColumnData = allColumns[colId];
                        break;
                    }
                }

                if (!exBlockerCardActualColumnData) {
                    console.warn("Ex-blocker card's column data not found, cannot update its activity log in DB.");
                } else {
                    const updatedExBlockerCardListInCol = exBlockerCardActualColumnData.cards.map(c =>
                        c.id === blockerCardIdToRemove ? {
                            ...c,
                            activity: updatedActivityForExBlocker
                        } : c
                    );

                    if (currentCardColumnId !== exBlockerCardActualColumnId) {
                        firestoreUpdates[`columns.${exBlockerCardActualColumnId}.cards`] = updatedExBlockerCardListInCol;
                    } else {
                        // Both cards in the same column. Merge updates.
                        // `updatedCurrentCardListInCol` already has the current card updated.
                        // Now, ensure the ex-blocker card in that same list also has its activity updated.
                        const finalCardsForSharedColumn = updatedCurrentCardListInCol.map(c => {
                            if (c.id === blockerCardIdToRemove) {
                                return { ...c, activity: updatedActivityForExBlocker };
                            }
                            return c;
                        });
                        firestoreUpdates[`columns.${currentCardColumnId}.cards`] = finalCardsForSharedColumn;
                    }
                }
            }

            await updateDoc(projectRef, firestoreUpdates);

            // Optimistically update current card's local state
            setCardData(prev => ({
                ...prev,
                blockedByCardIds: newBlockedByForCurrent,
                activity: updatedActivityForCurrent
            }));
            // Note: The "Blocking" list on the other card will update via Firestore sync.

        } catch (error) {
            console.error("Error removing blocker:", error);
            alert("Failed to remove blocker. " + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // --- End Blocked/Blocking Cards Logic ---

    // State for Add Related Card Dropdown
    const [showAddRelatedDropdown, setShowAddRelatedDropdown] = useState(false);
    const [relatedCardSearchTerm, setRelatedCardSearchTerm] = useState('');
    const addRelatedDropdownRef = React.useRef(null);
    const relatedCardSearchInputRef = React.useRef(null);


    // Compute available cards for "Add Related" dropdown
    const availableForManualRelation = React.useMemo(() => {
        if (!allColumns || !cardData || !cardData.id) return [];

        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }

        const currentRelatedIds = new Set(displayedRelatedCards.map(c => c.id));

        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false;
            return card.id !== cardData.id && !currentRelatedIds.has(card.id);
        });
    }, [allColumns, cardData, displayedRelatedCards]);

    const filteredManualRelationCandidates = React.useMemo(() => {
        if (!relatedCardSearchTerm) {
            return availableForManualRelation;
        }
        return availableForManualRelation.filter(card =>
            card.title.toLowerCase().includes(relatedCardSearchTerm.toLowerCase())
        );
    }, [availableForManualRelation, relatedCardSearchTerm]);

    // Effect for focusing search input when "Add Related" dropdown opens
    useEffect(() => {
        if (showAddRelatedDropdown && relatedCardSearchInputRef.current) {
            relatedCardSearchInputRef.current.focus();
        }
    }, [showAddRelatedDropdown]);

    // Effect for handling click outside of "Add Related" dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (addRelatedDropdownRef.current && !addRelatedDropdownRef.current.contains(event.target)) {
                setShowAddRelatedDropdown(false);
                setRelatedCardSearchTerm('');
            }
        };

        if (showAddRelatedDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showAddRelatedDropdown]);


    const handleSaveManualRelatedCard = async (selectedRelatedCardId) => {
        if (!selectedRelatedCardId || selectedRelatedCardId === cardData.id) {
            alert("Invalid selection for related card.");
            return;
        }

        const currentCardId = cardData.id;
        const currentCardManuallyRelatedIds = cardData.manuallyRelatedCardIds || [];

        if (currentCardManuallyRelatedIds.includes(selectedRelatedCardId)) {
            setShowAddRelatedDropdown(false);
            setRelatedCardSearchTerm('');
            return; // Already related
        }

        const selectedRelatedCardObject = findCardById(selectedRelatedCardId);
        if (!selectedRelatedCardObject) {
            alert("Selected related card not found. Cannot establish relation.");
            return;
        }
        const selectedRelatedCardCurrentRelatedIds = selectedRelatedCardObject.manuallyRelatedCardIds || [];

        // Check if already related from the other direction (e.g. data inconsistency)
        if (selectedRelatedCardCurrentRelatedIds.includes(currentCardId)) {
            if (!currentCardManuallyRelatedIds.includes(selectedRelatedCardId)) {
                 // Sync current card if it's missing the link
                 const newManuallyRelatedForCurrentOnly = [...currentCardManuallyRelatedIds, selectedRelatedCardId];
                 // Note: saveCardChanges updates the *current* card in the modal.
                 // It will correctly save its 'manuallyRelatedCardIds' and its activity.
                 const syncActivityDetails = `found existing relation from "${selectedRelatedCardObject.title}", synced link`;
                 const syncActivity = addActivity(user, { type: 'manual_relation_synced', details: syncActivityDetails });

                 await saveCardChanges({
                     manuallyRelatedCardIds: newManuallyRelatedForCurrentOnly,
                     activity: [...(cardData.activity || []), syncActivity]
                 });
            }
            setShowAddRelatedDropdown(false);
            setRelatedCardSearchTerm('');
            return;
        }

        setIsSaving(true);
        try {
            const projectRef = doc(db, 'projects', projectId);

            // Prepare updates for current card
            const newManuallyRelatedForCurrent = [...currentCardManuallyRelatedIds, selectedRelatedCardId];
            const activityDetailsCurrent = `linked to card "${selectedRelatedCardObject.title}" as related`;
            const newActivityForCurrent = addActivity(user, { type: 'manual_relation_added', details: activityDetailsCurrent });
            const updatedActivityForCurrent = [...(cardData.activity || []), newActivityForCurrent];

            // Prepare updates for selected related card
            const newManuallyRelatedForSelected = [...selectedRelatedCardCurrentRelatedIds, currentCardId];
            const activityDetailsSelected = `linked from card "${cardData.title}" as related`;
            const newActivityForSelected = addActivity(user, { type: 'manual_relation_added', details: activityDetailsSelected });
            // Ensure selectedRelatedCardObject.activity is an array before spreading
            const baseActivitySelected = Array.isArray(selectedRelatedCardObject.activity) ? selectedRelatedCardObject.activity : [];
            const updatedActivityForSelected = [...baseActivitySelected, newActivityForSelected];

            // 1. Update current card in its column
            const currentCardColumnId = selectedStatus;
            const currentCardCol = allColumns[currentCardColumnId];
            if (!currentCardCol) throw new Error("Current card's column not found");

            const updatedCurrentCardListInCol = currentCardCol.cards.map(c =>
                c.id === currentCardId ? {
                    ...c,
                    manuallyRelatedCardIds: newManuallyRelatedForCurrent,
                    activity: updatedActivityForCurrent
                } : c
            );

            // 2. Update selected related card in its column
            let selectedRelatedCardActualColumnId;
            let selectedRelatedCardActualColumnData;
            for (const colId in allColumns) {
                const foundCardInLoop = allColumns[colId].cards.find(c => c.id === selectedRelatedCardId);
                if (foundCardInLoop) {
                    selectedRelatedCardActualColumnId = colId;
                    selectedRelatedCardActualColumnData = allColumns[colId];
                    break;
                }
            }
            if (!selectedRelatedCardActualColumnData) throw new Error("Selected related card's column data not found");

            const updatedSelectedRelatedCardListInCol = selectedRelatedCardActualColumnData.cards.map(c =>
                c.id === selectedRelatedCardId ? {
                    ...c,
                    manuallyRelatedCardIds: newManuallyRelatedForSelected,
                    activity: updatedActivityForSelected
                } : c
            );

            const firestoreUpdates = { updatedAt: new Date().toISOString() };

            if (currentCardColumnId !== selectedRelatedCardActualColumnId) {
                firestoreUpdates[`columns.${currentCardColumnId}.cards`] = updatedCurrentCardListInCol;
                firestoreUpdates[`columns.${selectedRelatedCardActualColumnId}.cards`] = updatedSelectedRelatedCardListInCol;
            } else {
                // Both cards are in the same column. Merge the updates.
                // Start with current card updated array, then apply updates for the selected card.
                let finalCardsForSharedColumn = updatedCurrentCardListInCol.map(c => {
                    if (c.id === selectedRelatedCardId) { // This is the selected related card
                        return {
                            ...c, // It might have been the current card if IDs were same (already handled), or another card
                            manuallyRelatedCardIds: newManuallyRelatedForSelected,
                            activity: updatedActivityForSelected
                        };
                    }
                    return c; // This is either the (already updated) current card, or some other card
                });
                 firestoreUpdates[`columns.${currentCardColumnId}.cards`] = finalCardsForSharedColumn;
            }

            await updateDoc(projectRef, firestoreUpdates);

            // Optimistically update current card's local state
            setCardData(prev => ({
                ...prev,
                manuallyRelatedCardIds: newManuallyRelatedForCurrent,
                activity: updatedActivityForCurrent
            }));

            setShowAddRelatedDropdown(false);
            setRelatedCardSearchTerm('');

        } catch (error) {
            console.error("Error saving manual related card:", error);
            alert("Failed to add related card. " + error.message);
        } finally {
            setIsSaving(false);
        }
    };


    const handleCreateSubtaskFromChecklistItem = async (itemText) => {
        if (!itemText.trim()) {
            alert('Checklist item text is empty, cannot create subtask.');
            return;
        }

        setIsSaving(true); // Indicate saving process

        const newCardId = `card-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        // Use selectedStatus for the column ID, as this reflects the parent card's current column
        const newSubtaskColId = selectedStatus;

        const newCardObject = {
            id: newCardId,
            title: itemText.trim(),
            parentId: cardData.id,
            createdAt: new Date().toISOString(),
            description: '',
            assigneeId: null,
            labelIds: [],
            checklists: [],
            comments: [],
            activity: [], // Initial activity for the subtask itself could be added here
            dueDate: null,
            isDueDateCompleted: false,
            priority: cardData.priority || null, // Inherit parent's priority
            archived: false,
            coverImageId: null,
            // Default any other necessary card fields here
        };

        try {
            const projectRef = doc(db, 'projects', projectId);
            const targetColumn = allColumns[newSubtaskColId];

            if (!targetColumn) {
                console.error("Target column for new subtask from checklist item not found:", newSubtaskColId);
                alert("Error: Target column not found. Cannot save subtask.");
                setIsSaving(false);
                return;
            }

            const updatedCardsInTargetColumn = [...(targetColumn.cards || []), newCardObject];

            await updateDoc(projectRef, {
                [`columns.${newSubtaskColId}.cards`]: updatedCardsInTargetColumn,
                updatedAt: new Date().toISOString(),
            });

            // Refactored logic for atomic update
            // const projectRef = doc(db, 'projects', projectId); // This was redundant, projectRef is defined at the start of the try block.
            const updates = {
                updatedAt: new Date().toISOString(),
            };

            // Prepare new activity for the parent card
            const parentActivityDetails = `added subtask "${newCardObject.title}" from checklist item`;
            const newActivityForParent = addActivity(user, {
                type: 'child_task_added',
                details: parentActivityDetails,
            });
            const updatedParentCardActivities = [...(cardData.activity || []), newActivityForParent];

            // Get the current cards in the target column
            const currentColumnCards = allColumns[newSubtaskColId]?.cards || [];

            // Construct the new list of cards for the target column
            const newColumnCards = currentColumnCards
                .map(card => {
                    if (card.id === cardData.id) {
                        // This is the parent card, update its activity
                        return {
                            ...card,
                            activity: updatedParentCardActivities,
                        };
                    }
                    return card;
                })
                .concat(newCardObject); // Add the new subtask

            updates[`columns.${newSubtaskColId}.cards`] = newColumnCards;

            await updateDoc(projectRef, updates);

            // Optimistically update parent card's activity in local state
            setCardData(prevCardData => ({
                ...prevCardData,
                activity: updatedParentCardActivities,
            }));

        } catch (error) {
            console.error('Error creating new subtask from checklist item:', error);
            alert('Failed to create subtask from checklist item. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // --- Parent Dropdown Population & Filtering ---
    const availableParentCards = React.useMemo(() => {
        if (!allColumns || !cardData || !cardData.id) return [];

        const descendants = getAllDescendantIds(cardData.id, allColumns);
        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }

        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false; // Ensure card and card.id are valid
            return card.id !== cardData.id && !descendants.has(card.id);
        });
    }, [allColumns, cardData, cardData.id]); // getAllDescendantIds is stable if its args are

    const filteredParentCandidates = React.useMemo(() => {
        if (!parentSearchTerm) {
            return availableParentCards;
        }
        return availableParentCards.filter(card =>
            card.title.toLowerCase().includes(parentSearchTerm.toLowerCase())
        );
    }, [availableParentCards, parentSearchTerm]);
    // --- End Parent Dropdown Population & Filtering ---

    const handleShowCoverImagePopover = () => {
        setShowCoverImagePopover(!showCoverImagePopover);
        // Close other popovers if open
        setShowLabelPicker(false);
        setShowAssigneePicker(false);
        setShowDueDatePopover(false);
    };

    const handleImageFileSelected = async (event) => {
        const file = event.target.files[0];
        // Adjusted MAX_BINARY_SIZE_FOR_FIREBASE to account for Base64 overhead (approx 0.70MB for binary)
        const MAX_BINARY_SIZE_FOR_FIREBASE = Math.floor(0.70 * 1024 * 1024);

        if (!file) {
            return;
        }

        setIsLoadingCoverImage(true); // Indicate processing starts

        const options = {
            maxSizeMB: 0.70, // Target compression to ~0.70MB
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            initialQuality: 0.7
        };

        try {
            const compressedFile = await imageCompression(file, options);

            if (compressedFile.size > MAX_BINARY_SIZE_FOR_FIREBASE) {
                alert("Image is too large for storage, even after compression. Please choose a smaller file (target < 0.7MB).");
                setIsLoadingCoverImage(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = ""; // Reset file input
                }
                return;
            }

            const reader = new FileReader();
            reader.onloadend = async () => {
                const base64String = reader.result;
                try {
                    if (cardData.coverImageId) {
                        await deleteCoverImage(cardData.coverImageId);
                    }
                    const imageId = await addCoverImage(base64String);
                    await saveCardChanges(
                        { coverImageId: imageId },
                        addActivity(user, { type: 'cover_image', details: 'added a cover image' })
                    );
                    setCoverImageBase64(base64String);
                    setCardData(prev => ({ ...prev, coverImageId: imageId }));
                    localStorage.setItem(`coverImage_${cardData.id}`, base64String);
                    localStorage.setItem(`coverImageId_${cardData.id}`, imageId);
                    setShowCoverImagePopover(false);
                } catch (error) {
                    console.error("Error processing image upload:", error);
                    alert("Failed to upload cover image.");
                } finally {
                    setIsLoadingCoverImage(false); // Ensure loading stops
                }
            };
            reader.readAsDataURL(compressedFile); // Use compressedFile

        } catch (error) {
            console.error('Error compressing image:', error);
            alert('Failed to compress image. Please try again or select a different image.');
            setIsLoadingCoverImage(false);
        } finally {
            // Reset file input to allow selecting the same file again if needed
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handlePasteImage = async (event) => {
        const items = (event.clipboardData || event.originalEvent.clipboardData)?.items;
        if (!items) return;

        for (let index in items) {
            const item = items[index];
            if (item.kind === 'file' && item.type.startsWith('image/')) {
                event.preventDefault(); // Prevent default paste action
                const file = item.getAsFile();
                // Adjusted MAX_BINARY_SIZE_FOR_FIREBASE to account for Base64 overhead (approx 0.70MB for binary)
                const MAX_BINARY_SIZE_FOR_FIREBASE = Math.floor(0.70 * 1024 * 1024);

                if (!file) return;

                setIsLoadingCoverImage(true); // Indicate processing starts

                const options = {
                    maxSizeMB: 0.70, // Target compression to ~0.70MB
                    maxWidthOrHeight: 1920,
                    useWebWorker: true,
                    initialQuality: 0.7
                };

                try {
                    const compressedFile = await imageCompression(file, options);

                    if (compressedFile.size > MAX_BINARY_SIZE_FOR_FIREBASE) {
                        alert("Pasted image is too large for storage, even after compression. Please use a smaller image (target < 0.7MB).");
                        setIsLoadingCoverImage(false);
                        return; // Abort if too large
                    }

                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        const base64String = reader.result;
                        try {
                            if (cardData.coverImageId) {
                                await deleteCoverImage(cardData.coverImageId);
                            }
                            const imageId = await addCoverImage(base64String);
                            await saveCardChanges(
                                { coverImageId: imageId },
                                addActivity(user, { type: 'cover_image', details: 'pasted a cover image' })
                            );
                            setCoverImageBase64(base64String);
                            setCardData(prev => ({ ...prev, coverImageId: imageId }));
                            localStorage.setItem(`coverImage_${cardData.id}`, base64String);
                            localStorage.setItem(`coverImageId_${cardData.id}`, imageId);
                            setShowCoverImagePopover(false);
                        } catch (error) {
                            console.error("Error processing pasted image:", error);
                            alert("Failed to paste cover image.");
                        } finally {
                            setIsLoadingCoverImage(false); // Ensure loading stops
                        }
                    };
                    reader.readAsDataURL(compressedFile); // Use compressedFile
                    return; // Process only the first image found

                } catch (error) {
                    console.error('Error compressing pasted image:', error);
                    alert('Failed to compress pasted image. Please try again or use a different image.');
                    setIsLoadingCoverImage(false);
                    return; // Abort on compression error
                }
            }
        }
    };

    const handleRemoveCoverImage = async () => {
        if (!cardData.coverImageId) return;
        setIsLoadingCoverImage(true);
        try {
            await deleteCoverImage(cardData.coverImageId);
            await saveCardChanges(
                { coverImageId: null },
                addActivity(user, { type: 'cover_image', details: 'removed the cover image' })
            );
            setCoverImageBase64(null);
            setCardData(prev => ({ ...prev, coverImageId: null }));
            localStorage.removeItem(`coverImage_${cardData.id}`);
            localStorage.removeItem(`coverImageId_${cardData.id}`);
            setShowCoverImagePopover(false);
        } catch (error) {
            console.error("Error removing cover image:", error);
            alert("Failed to remove cover image.");
        } finally {
            setIsLoadingCoverImage(false);
        }
    };


    const handleOpenMoveCardModal = async () => {
        if (!projectId) {
            console.error("Project ID is missing, cannot fetch workspace ID.");
            // Optionally, show an error to the user
            return;
        }
        setIsLoadingWorkspaceId(true);
        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            if (projectSnap.exists()) {
                const projectData = projectSnap.data();
                if (projectData.workspaceId) {
                    setCurrentWorkspaceId(projectData.workspaceId);
                    setShowMoveCardModal(true);
                } else {
                    console.error("Workspace ID not found in project data.");
                    // Optionally, show an error to the user
                }
            } else {
                console.error("Project data not found, cannot determine workspace ID.");
                // Optionally, show an error to the user
            }
        } catch (error) {
            console.error("Error fetching project data for workspace ID:", error);
            // Optionally, show an error to the user
        } finally {
            setIsLoadingWorkspaceId(false);
        }
    };

    const hasAnyRelations = useMemo(() => {
        return !!(
            (cardData.parentId && parentCard) ||
            (subtasks && subtasks.length > 0) ||
            (displayedRelatedCards && displayedRelatedCards.length > 0) ||
            (blockedByCards && blockedByCards.length > 0) ||
            (blockingCards && blockingCards.length > 0)
        );
    }, [cardData.parentId, parentCard, subtasks, displayedRelatedCards, blockedByCards, blockingCards]);


    return (
        <ModalOverlay onClick={onClose}>
            <DragDropContext onDragEnd={onDragEnd}>
                <ModalContent onClick={e => e.stopPropagation()}>
                    <MainContent>
                        <CloseButton onClick={onClose}>×</CloseButton>

                        {/* Display Cover Image if available */}
                        {!isLoadingCoverImage && coverImageBase64 && (
                            <CoverImageWrapper>
                                <ModalCoverImage src={coverImageBase64} alt="Card cover" />
                                <DirectRemoveCoverButton
                                    onClick={handleRemoveCoverImage}
                                    title="Remove cover image"
                                >
                                    ×
                                </DirectRemoveCoverButton>
                            </CoverImageWrapper>
                        )}
                        {/* Placeholder or spinner while loading cover image */}
                        {isLoadingCoverImage && (
                            <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#22272B', marginBottom: '16px', borderRadius: '8px' }}>
                                <p>Loading cover...</p>
                            </div>
                        )}

                        {/* Due Date Popover */}
                        {showDueDatePopover && (
                            <DueDatePopover>
                                <PopoverCloseButton onClick={toggleDueDatePopover}>×</PopoverCloseButton>
                                <DatePicker
                                    selected={selectedDueDate || (cardData.dueDate ? new Date(cardData.dueDate) : null)}
                                    onChange={handleRawDueDateChange}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    inline
                                    minDate={cardData.startDate ? new Date(cardData.startDate) : null}
                                />
                                <DueDateButtonContainer>
                                    <DueDateButton onClick={saveDueDate}>Save</DueDateButton>
                                    <DueDateRemoveButton onClick={clearDueDate}>Remove</DueDateRemoveButton>
                                </DueDateButtonContainer>
                            </DueDatePopover>
                        )}

                        {/* Start Date Popover */}
                        {showStartDatePopover && (
                            <DueDatePopover>
                                <PopoverCloseButton onClick={toggleStartDatePopover}>×</PopoverCloseButton>
                                <DatePicker
                                    selected={selectedStartDate || (cardData.startDate ? new Date(cardData.startDate) : null)}
                                    onChange={handleRawStartDateChange}
                                    showTimeSelect
                                    timeFormat="HH:mm"
                                    dateFormat="MMMM d, yyyy h:mm aa"
                                    inline
                                    maxDate={cardData.dueDate ? new Date(cardData.dueDate) : null}
                                />
                                <DueDateButtonContainer>
                                    <DueDateButton onClick={saveStartDate}>Save</DueDateButton>
                                    <DueDateRemoveButton onClick={clearStartDate}>Remove</DueDateRemoveButton>
                                </DueDateButtonContainer>
                            </DueDatePopover>
                        )}

                        <CardHeader
                            cardData={cardData}
                            initialCard={initialCard}
                            allColumns={allColumns}
                            selectedStatus={selectedStatus}
                            selectedPriority={selectedPriority}
                            assignee={assignee}
                            labels={labels}
                            showAssigneePicker={showAssigneePicker}
                            setShowAssigneePicker={setShowAssigneePicker}
                            setShowLabelPicker={setShowLabelPicker}
                            handleStatusChange={handleStatusChange}
                            handlePriorityChange={handlePriorityChange}
                            handleToggleLabel={handleToggleLabel}
                            saveCardChanges={saveCardChanges}
                            setCardData={setCardData}
                            showStatusDropdown={showStatusDropdown}
                            setShowStatusDropdown={setShowStatusDropdown}
                            showPriorityDropdown={showPriorityDropdown}
                            setShowPriorityDropdown={setShowPriorityDropdown}
                            columnOrder={columnOrder}
                            handleUnassign={handleUnassign} // Pass handleUnassign to CardHeader
                        />

                        {isMobile && (
                            <MobileCardSidebar
                                isExpanded={isMobileSectionsExpanded}
                                setIsExpanded={setIsMobileSectionsExpanded}
                                setShowLabelPicker={setShowLabelPicker}
                                handleAddChecklist={handleAddChecklist}
                                showAssigneePicker={showAssigneePicker}
                                setShowAssigneePicker={setShowAssigneePicker}
                                assignee={assignee}
                                projectMembers={projectMembers}
                                handleAssign={handleAssign}
                                handleUnassign={handleUnassign}
                                onToggleDueDatePopover={toggleDueDatePopover}
                                onToggleStartDatePopover={toggleStartDatePopover}
                                onShowCoverImagePopover={handleShowCoverImagePopover}
                                onOpenMoveCardModal={handleOpenMoveCardModal}
                                isLoadingWorkspaceId={isLoadingWorkspaceId}
                                handleDuplicateCard={handleDuplicateCard}
                                handleArchiveCard={handleArchiveCard}
                                onClose={onClose}
                                setShowDeleteConfirmation={setShowDeleteConfirmation}
                                cardData={cardData}
                                columnTitle={column?.title}
                                projectLabels={labels}
                            />
                        )}

                        <CardDescription
                            cardData={cardData}
                            setCardData={setCardData}
                            saveCardChanges={saveCardChanges}
                            onAddAttachment={handleAddAttachment}
                            user={user}
                        />

                        {/* GitHub URL Input Section (Conditionally Rendered) */}
                        {enableGithubLinks && (
                             <Section>
                                <SectionTitle>
                                    <GithubIcon />
                                    GitHub Pull Request
                                </SectionTitle>
                                {isEditingGithubUrl || !githubUrl ? (
                                    <StyledInput
                                        id="githubUrl"
                                        type="url"
                                        placeholder="Paste GitHub PR link here..."
                                        value={githubUrl}
                                        onChange={(e) => setGithubUrl(e.target.value)}
                                        onPaste={async (e) => {
                                            const pastedText = e.clipboardData.getData('text').trim();
                                            const oldUrl = initialCard.githubUrl || '';

                                            if (isValidUrl(pastedText)) {
                                                setGithubUrl(pastedText); // Update state immediately
                                                if (pastedText !== oldUrl) {
                                                    let activityActionDescriptor = {};
                                                    if (pastedText && !oldUrl) { // URL added
                                                        activityActionDescriptor = { type: 'github_link_added', details: pastedText };
                                                    } else if (pastedText && oldUrl) { // URL updated
                                                        activityActionDescriptor = { type: 'github_link_updated', details: pastedText };
                                                    }
                                                    // No case for removing via paste, as pasting an empty string is not a typical "remove" action.
                                                    // Removal is handled by handleGithubUrlBlur when field is cleared and blurred.
                                                    if (Object.keys(activityActionDescriptor).length > 0) {
                                                        const fullActivityLog = addActivity(user, activityActionDescriptor);
                                                        await saveCardChanges({ githubUrl: pastedText }, fullActivityLog);
                                                    } else if (pastedText === oldUrl) {
                                                        // Pasted the same URL, no actual change, so no save/activity needed.
                                                        // This can happen if initialCard.githubUrl was already pastedText.
                                                    }
                                                }
                                                // We might want to keep the input field visible for a moment or
                                                // let the user decide to click away/press enter.
                                                // For now, saving directly and relying on blur/enter for exiting edit mode.
                                                // If the save is successful, initialCard.githubUrl will be updated
                                                // in the parent component, and then initialCard prop will reflect this.
                                            } else {
                                                // Handle invalid URL paste if needed, e.g., show a message
                                                // or just let the normal validation on blur handle it.
                                                // For now, just update the state and let blur/enter validation catch it.
                                                setGithubUrl(pastedText);
                                            }
                                        }}
                                        onBlur={handleGithubUrlBlur}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleGithubUrlBlur();
                                            } else if (e.key === 'Escape') {
                                                setGithubUrl(initialCard.githubUrl || '');
                                                setIsEditingGithubUrl(false);
                                            }
                                        }}
                                    />
                                ) : (
                                    <ClickableLinkWrapper>
                                        <LinkDisplay
                                            href={isValidUrl(githubUrl) ? githubUrl : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => { if (!isValidUrl(githubUrl)) e.preventDefault();}}
                                            title={githubUrl}
                                        >
                                            {githubUrl}
                                        </LinkDisplay>
                                        <EditLinkButton
                                            onClick={() => setIsEditingGithubUrl(true)}
                                            title="Edit GitHub URL"
                                        >
                                            <EditIcon />
                                        </EditLinkButton>
                                    </ClickableLinkWrapper>
                                )}
                            </Section>
                        )}

                        {/* Card Relations Section */}
                        <Section>
                            <RelationsSectionHeader>
                                <RelationsSectionTitle
                                    onClick={() => {
                                        if (hasAnyRelations) { // Only allow toggle if there are relations
                                            setIsRelationsExpanded(!isRelationsExpanded);
                                        }
                                    }}
                                    isExpanded={isRelationsExpanded && hasAnyRelations} // Chevron direction based on actual expansion
                                    hasRelations={hasAnyRelations} // Pass this for styling or disabling chevron
                                >
                                    {hasAnyRelations && <ChevronIcon direction={(isRelationsExpanded && hasAnyRelations) ? 'down' : 'right'} />}
                                    Card Relations
                                </RelationsSectionTitle>
                                <div style={{ position: 'relative' }}> {/* Wrapper for dropdown positioning */}
                                    <AddRelationButton onClick={() => setShowAddRelationDropdown(prev => !prev)}>
                                        <PlusIcon width={16} height={16} /> Add New Relation
                                    </AddRelationButton>
                                    {showAddRelationDropdown && (
                                        <RelationTypeDropdown ref={addRelationDropdownRef}>
                                            <RelationTypeDropdownItem onClick={() => { handleOpenSetParentModal(); setShowAddRelationDropdown(false); }}>
                                                Set Parent Task
                                            </RelationTypeDropdownItem>
                                            <RelationTypeDropdownItem onClick={() => { handleOpenAddSubtaskModal(); setShowAddRelationDropdown(false); }}>
                                                Add Subtask
                                            </RelationTypeDropdownItem>
                                            <RelationTypeDropdownItem onClick={() => { handleOpenAddRelatedCardModal(); setShowAddRelationDropdown(false); }}>
                                                Add Related Card
                                            </RelationTypeDropdownItem>
                                            <RelationTypeDropdownItem onClick={() => { handleOpenAddBlockerModal(); setShowAddRelationDropdown(false); }}>
                                                Add Blocker
                                            </RelationTypeDropdownItem>
                                        </RelationTypeDropdown>
                                    )}
                                </div>
                            </RelationsSectionHeader>

                            {isRelationsExpanded && hasAnyRelations && ( // Content only shown if expandable and has relations
                                <>
                                    {/* Parent Task Section - Conditionally Rendered */}
                                    {cardData.parentId && parentCard && ( // Condition to render section
                                        <Section>
                                            <SectionTitle>
                                                Parent Task
                                            </SectionTitle>
                                            <ClickableLinkWrapper>
                                                <LinkDisplay
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        if (onSwitchCard) {
                                                            onSwitchCard(parentCard.id);
                                                        } else {
                                                            console.warn("onSwitchCard prop not provided to CardModal for parent navigation.");
                                                            alert(`DEBUG: Would switch to parent card: ${parentCard.title} (ID: ${parentCard.id})`);
                                                            onClose();
                                                        }
                                                    }}
                                                    title={`Go to parent card: ${parentCard.title}`}
                                                >
                                                    {parentCard.title}
                                                </LinkDisplay>
                                                <Button // Using Button styled component for "Remove"
                                                    onClick={handleRemoveParent}
                                                    style={{ background: '#6c757d', marginLeft: '10px', padding: '6px 10px', fontSize: '13px' }}
                                                    title="Remove Parent"
                                                >
                                                    Remove
                                                </Button>
                                            </ClickableLinkWrapper>
                                        {/* Old ParentDropdownContainer removed, functionality moved to SetParentModal */}
                                        {/* "No parent task set" message is implicitly handled by the section not rendering if no parent exists */}
                                    </Section>
                                    )}

                                    {/* Subtasks Section - Conditionally Rendered */}
                                    {subtasks.length > 0 && (
                                    <Section>
                                        <SectionTitle>
                                            Subtasks ({subtasks.length})
                                        </SectionTitle>
                                        <ul className="custom-scrollbar" style={{ listStyle: 'none', paddingLeft: '0', marginBottom: '16px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {subtasks.map(subtask => (
                                                <li key={subtask.id} style={{ marginBottom: '8px' }}>
                                                    <ClickableLinkWrapper>
                                                        <LinkDisplay
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (onSwitchCard) {
                                                                    onSwitchCard(subtask.id);
                                                                } else {
                                                                    console.warn("onSwitchCard prop not provided to CardModal for subtask navigation.");
                                                                    alert(`DEBUG: Would switch to subtask: ${subtask.title} (ID: ${subtask.id})`);
                                                                    onClose();
                                                                }
                                                            }}
                                                            title={`Go to subtask: ${subtask.title}`}
                                                        >
                                                            {subtask.title}
                                                        </LinkDisplay>
                                                    </ClickableLinkWrapper>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* 'No subtasks yet' message is implicitly handled by section not rendering */}
                                    </Section>
                                    )}

                                    {/* Related Cards Section - Conditionally Rendered */}
                                    {displayedRelatedCards.length > 0 && (
                                    <Section>
                                        <SectionTitle>
                                            Related Cards ({displayedRelatedCards.length})
                                        </SectionTitle>
                                        <ul className="custom-scrollbar" style={{ listStyle: 'none', paddingLeft: '0', marginBottom: '16px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {displayedRelatedCards.map(relatedCard => (
                                                <li key={relatedCard.id} style={{ marginBottom: '8px' }}>
                                                    <ClickableLinkWrapper>
                                                        <LinkDisplay
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (onSwitchCard) {
                                                                    onSwitchCard(relatedCard.id);
                                                                } else {
                                                                    console.warn("onSwitchCard prop not provided for related card navigation.");
                                                                    onClose();
                                                                }
                                                            }}
                                                            title={`Go to related card: ${relatedCard.title}`}
                                                        >
                                                            {relatedCard.title}
                                                        </LinkDisplay>
                                                    </ClickableLinkWrapper>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* 'No related cards yet' message handled by section not rendering */}
                                    </Section>
                                    )}
                                    {/* Old ParentDropdownContainer for adding related cards is removed */}

                                    {/* Blocked By Section - Conditionally Rendered */}
                                    {blockedByCards.length > 0 && (
                                    <Section>
                                        <SectionTitle>
                                            Blocked By ({blockedByCards.length})
                                        </SectionTitle>
                                        <ul className="custom-scrollbar" style={{ listStyle: 'none', paddingLeft: '0', marginBottom: '16px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {blockedByCards.map(blockerCard => (
                                                <li key={blockerCard.id} style={{ marginBottom: '8px' }}>
                                                    <ClickableLinkWrapper>
                                                        <LinkDisplay
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (onSwitchCard) onSwitchCard(blockerCard.id);
                                                                else onClose();
                                                            }}
                                                            title={`Go to card: ${blockerCard.title}`}
                                                        >
                                                            {blockerCard.title}
                                                        </LinkDisplay>
                                                        <Button onClick={() => handleRemoveBlocker(blockerCard.id)} style={{ background: '#6c757d', marginLeft: '10px', padding: '3px 6px', fontSize: '12px' }}>
                                                            Remove
                                                        </Button>
                                                    </ClickableLinkWrapper>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* 'Not blocked by any cards' message handled by section not rendering */}
                                    </Section>
                                    )}
                                    {/* Old ParentDropdownContainer for adding blockers is removed */}

                                    {/* Blocking Section - Conditionally Rendered */}
                                    {blockingCards.length > 0 && (
                                    <Section>
                                        <SectionTitle>
                                            Blocking ({blockingCards.length})
                                        </SectionTitle>
                                        <ul className="custom-scrollbar" style={{ listStyle: 'none', paddingLeft: '0', marginBottom: '16px', maxHeight: '150px', overflowY: 'auto' }}>
                                            {blockingCards.map(blockedCard => (
                                                <li key={blockedCard.id} style={{ marginBottom: '8px' }}>
                                                    <ClickableLinkWrapper>
                                                        <LinkDisplay
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (onSwitchCard) onSwitchCard(blockedCard.id);
                                                                else onClose();
                                                            }}
                                                            title={`Go to card: ${blockedCard.title}`}
                                                        >
                                                            {blockedCard.title}
                                                        </LinkDisplay>
                                                    </ClickableLinkWrapper>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* 'Not blocking any cards' message handled by section not rendering */}
                                    </Section>
                                    )}
                                </>
                            )}
                        </Section>

                        <CardStartDate
                            cardData={cardData}
                        />

                        <CardDueDate
                            project={project}
                            cardData={cardData}
                            setCardData={setCardData}
                            saveCardChanges={saveCardChanges}
                        />

                        <AttachmentSection
                            attachments={cardData.attachments}
                            onAddAttachment={handleAddAttachment}
                            onDeleteAttachment={handleDeleteAttachment}
                        />

                        <CardChecklists
                            cardData={cardData}
                            setCardData={setCardData}
                            saveCardChanges={saveCardChanges}
                            handleDeleteChecklist={handleDeleteChecklist}
                            handleAddChecklistItem={handleAddChecklistItem}
                            toggleChecklistItem={toggleChecklistItem}
                            handleDeleteChecklistItem={handleDeleteChecklistItem}
                            handleUpdateChecklistItem={handleUpdateChecklistItem}
                            handleCreateSubtaskFromChecklistItem={handleCreateSubtaskFromChecklistItem} // Pass the new handler
                        />

                        <CardActivity
                            cardData={cardData}
                            comment={comment}
                            setComment={setComment}
                            handleAddComment={handleAddComment}
                            handleUpdateComment={handleUpdateComment} // Pass handleUpdateComment
                            isSaving={isSaving}
                            onAddAttachment={handleAddAttachment}
                            projectMembers={projectMembers} // Pass projectMembers
                        />
                    </MainContent>

                    {!isMobile && (
                        <CardSidebar
                            setShowLabelPicker={setShowLabelPicker}
                            handleAddChecklist={handleAddChecklist}
                        showAssigneePicker={showAssigneePicker}
                        setShowAssigneePicker={setShowAssigneePicker}
                        showStatusDropdown={showStatusDropdown}
                        setShowStatusDropdown={setShowStatusDropdown}
                        showPriorityDropdown={showPriorityDropdown}
                        setShowPriorityDropdown={setShowPriorityDropdown}
                        selectedStatus={selectedStatus}
                        selectedPriority={selectedPriority}
                        allColumns={allColumns}
                        assignee={assignee}
                        cardData={cardData}
                        handleArchiveCard={handleArchiveCard}
                        handleDuplicateCard={handleDuplicateCard}
                        onClose={onClose}
                        setShowDeleteConfirmation={setShowDeleteConfirmation}
                        handleStatusChange={handleStatusChange}
                        handlePriorityChange={handlePriorityChange}
                        projectMembers={projectMembers}
                        handleAssign={handleAssign}
                        handleUnassign={handleUnassign} // Pass handleUnassign
                        columnOrder={columnOrder}
                        handleNextStatus={handleNextStatus}
                        onToggleDueDatePopover={toggleDueDatePopover}
                        onToggleStartDatePopover={toggleStartDatePopover}
                        onOpenMoveCardModal={handleOpenMoveCardModal} // Pass the handler
                        isLoadingWorkspaceId={isLoadingWorkspaceId} // Pass loading state
                        onShowCoverImagePopover={handleShowCoverImagePopover} // Pass handler for cover image popover
                        columnTitle={column?.title}
                        projectLabels={labels}
                        projectMembers={projectMembers}
                    />
                    )}

                    {showCoverImagePopover && (
                        <CoverImagePopover onPaste={handlePasteImage}>
                             <PopoverCloseButton onClick={() => setShowCoverImagePopover(false)}>×</PopoverCloseButton>
                            <SectionTitle>Card Cover</SectionTitle>
                            {isLoadingCoverImage && <p>Loading image...</p>}
                            {!isLoadingCoverImage && coverImageBase64 && (
                                <CoverImagePreview src={coverImageBase64} alt="Cover image" />
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                ref={fileInputRef}
                                onChange={handleImageFileSelected}
                            />
                            <UploadButton onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={isLoadingCoverImage}>
                                <UploadIcon /> Upload an image
                            </UploadButton>
                            <p style={{ textAlign: 'center', fontSize: '12px', margin: '8px 0' }}>Or paste an image (Ctrl+V / Cmd+V)</p>
                            {coverImageBase64 && (
                                <RemoveCoverButton onClick={handleRemoveCoverImage} disabled={isLoadingCoverImage}>
                                    Remove Cover
                                </RemoveCoverButton>
                            )}
                        </CoverImagePopover>
                    )}

                    {showMoveCardModal && currentWorkspaceId && (
                        <MoveCardModal
                            cardData={cardData}
                            currentProjectId={projectId}
                            currentColumnId={selectedStatus} // Use selectedStatus for current column ID
                            currentColumnTitle={allColumns[selectedStatus]?.title} // Pass current column title
                            currentWorkspaceId={currentWorkspaceId}
                            onClose={() => setShowMoveCardModal(false)}
                            onCardMoved={() => {
                                setShowMoveCardModal(false);
                                onClose();
                            }}
                        />
                    )}

                    {/* Render New Modals */}
                    {showSetParentModal && (
                        <SetParentModal
                            isOpen={showSetParentModal}
                            onClose={handleCloseSetParentModal}
                            projectId={projectId}
                            currentCardId={cardData.id}
                            allColumns={allColumns}
                            onParentSet={handleParentSetInModal}
                        />
                    )}

                    {showAddRelatedCardModal && (
                        <AddRelatedCardModal
                            isOpen={showAddRelatedCardModal}
                            onClose={handleCloseAddRelatedCardModal}
                            projectId={projectId}
                            currentCardId={cardData.id}
                            currentCardTitle={cardData.title}
                            currentCardManuallyRelatedIds={cardData.manuallyRelatedCardIds || []}
                            allColumns={allColumns}
                            onRelatedCardAdded={handleRelatedCardAddedInModal}
                        />
                    )}

                    {showAddBlockerModal && (
                        <AddBlockerModal
                            isOpen={showAddBlockerModal}
                            onClose={handleCloseAddBlockerModal}
                            projectId={projectId}
                            currentCardId={cardData.id}
                            currentCardTitle={cardData.title}
                            currentCardBlockedByIds={cardData.blockedByCardIds || []}
                            allColumns={allColumns}
                            onBlockerAdded={handleBlockerAddedInModal}
                        />
                    )}

                    {showAddSubtaskModal && (
                        <AddSubtaskModal
                            isOpen={showAddSubtaskModal}
                            onClose={handleCloseAddSubtaskModal}
                            projectId={projectId}
                            parentId={cardData.id}
                            parentCardTitle={cardData.title}
                            parentCardPriority={cardData.priority}
                            allColumns={allColumns}
                            columnOrder={columnOrder}
                            onSubtaskAdded={handleSubtaskAddedInModal}
                        />
                    )}

                    {showLabelPicker && (
                        <LabelPicker
                            labels={labels}
                            selectedLabels={cardData.labelIds}
                            onClose={() => setShowLabelPicker(false)}
                            onCreateLabel={handleCreateLabel}
                            onToggleLabel={handleToggleLabel}
                        />
                    )}

                    {showDeleteConfirmation && (
                        <ConfirmationModal
                            title="Delete Card"
                            message="Are you sure you want to delete this card? This action cannot be undone."
                            onConfirm={handleDeleteCard}
                            onCancel={() => setShowDeleteConfirmation(false)}
                        />
                    )}

                </ModalContent>
            </DragDropContext>
        </ModalOverlay>
    );
};

export default CardModal;
